import React, { useState, useEffect } from "react";
import "./UsageLimit.css";
import Sidebar from "../../../Navigation/Sidebar/Sidebar";

const UsageLimitComponent = (props) => {
  const {
    loading,
    usageLimitData,
    isEditMode,
    formData,
    handleBackClick,
    handleEditClick,
    handleCancelClick,
    handleInputChange,
    handleUpdateLimit,
    corporateId
  } = props;

  const [localLimits, setLocalLimits] = useState([]);
  const [localUsageLimitRequired, setLocalUsageLimitRequired] = useState(false);
  const [savedLimits, setSavedLimits] = useState([]);
  const [userRoleId, setUserRoleId] = useState(null);

  // Get user role from localStorage
  useEffect(() => {
    try {
      const userData = JSON.parse(localStorage.getItem("data"));
      if (userData && userData.details && userData.details.userRole) {
        setUserRoleId(userData.details.userRole.role_id);
      }
    } catch (error) {
      console.error("Error parsing user data from localStorage:", error);
    }
  }, []);

  // Check if user can perform actions (hide for role_id 15)
  const canPerformActions = userRoleId !== 15 && userRoleId !== null;

  useEffect(() => {
    if (formData) {
      setLocalUsageLimitRequired(formData.usageLimitRequired || false);
      setLocalLimits(formData.limits || []);
      if (formData.limits && formData.limits.length > 0) {
        setSavedLimits(JSON.parse(JSON.stringify(formData.limits)));
      }
    }
  }, [formData]);

  const getFrequencyDisplay = (frequency) => {
    if (!frequency) return "N/A";
    if (frequency === "HOURS") return "Hour";
    if (frequency === "DAYS") return "Day";
    if (frequency === "MONTHS") return "Month";
    return frequency;
  };

  const getLimitTypeDisplay = (limitOn) => {
    if (limitOn === "AMOUNT") return "Amount";
    if (limitOn === "TRANSACTION") return "Transaction";
    return limitOn || "N/A";
  };

  const getDurationUnit = (frequency) => {
    if (frequency === "HOURS") return "Hour(s)";
    if (frequency === "DAYS") return "Day(s)";
    if (frequency === "MONTHS") return "Month(s)";
    return "";
  };

  const hasBothLimitTypes = () => {
    const hasAmount = localLimits.some(limit => limit.limitOn === "AMOUNT");
    const hasTransaction = localLimits.some(limit => limit.limitOn === "TRANSACTION");
    return hasAmount && hasTransaction;
  };

  const canAddMoreLimits = () => {
    if (localLimits.length >= 2) return false;
    return !hasBothLimitTypes();
  };

  const handleUsageLimitRequiredChange = (value) => {
    const isRequired = value === "true";
    
    setLocalUsageLimitRequired(isRequired);
    
    if (isRequired) {
      if (savedLimits.length > 0) {
        setLocalLimits(JSON.parse(JSON.stringify(savedLimits)));
        handleInputChange("limits", JSON.parse(JSON.stringify(savedLimits)));
      } else if (localLimits.length === 0) {
        const defaultLimit = {
          limitOn: "AMOUNT",
          frequency: "DAYS",
          duration: "1",
          value: ""
        };
        setLocalLimits([defaultLimit]);
        handleInputChange("limits", [defaultLimit]);
      } else {
        handleInputChange("limits", localLimits);
      }
    } else {
      if (localLimits.length > 0) {
        setSavedLimits(JSON.parse(JSON.stringify(localLimits)));
      }
      setLocalLimits([]);
      handleInputChange("limits", []);
    }
    
    handleInputChange("usageLimitRequired", isRequired);
  };

  const handleLimitOnChange = (index, value) => {
    const updatedLimits = [...localLimits];
    updatedLimits[index] = {
      ...updatedLimits[index],
      limitOn: value
    };
    setLocalLimits(updatedLimits);
    handleInputChange("limits", updatedLimits);
  };

  const handleFrequencyChange = (index, value) => {
    const updatedLimits = [...localLimits];
    updatedLimits[index] = {
      ...updatedLimits[index],
      frequency: value,
      duration: "1"
    };
    setLocalLimits(updatedLimits);
    handleInputChange("limits", updatedLimits);
  };

  const handleDurationChange = (index, value) => {
    const updatedLimits = [...localLimits];
    updatedLimits[index] = {
      ...updatedLimits[index],
      duration: value
    };
    setLocalLimits(updatedLimits);
    handleInputChange("limits", updatedLimits);
  };

  const handleNumberInputChange = (index, e) => {
    const value = e.target.value;
    const updatedLimits = [...localLimits];
    updatedLimits[index] = {
      ...updatedLimits[index],
      value: value
    };
    setLocalLimits(updatedLimits);
    handleInputChange("limits", updatedLimits);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    e.target.blur();
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
    }
  };

  const handleAddNewLimit = () => {
    const hasAmount = localLimits.some(limit => limit.limitOn === "AMOUNT");
    const hasTransaction = localLimits.some(limit => limit.limitOn === "TRANSACTION");
    
    let newLimitType = "AMOUNT";
    if (hasAmount && !hasTransaction) {
      newLimitType = "TRANSACTION";
    } else if (!hasAmount && hasTransaction) {
      newLimitType = "AMOUNT";
    }
    
    const newLimit = {
      limitOn: newLimitType,
      frequency: "DAYS",
      duration: "1",
      value: ""
    };
    const updatedLimits = [...localLimits, newLimit];
    setLocalLimits(updatedLimits);
    handleInputChange("limits", updatedLimits);
  };

  const handleRemoveLimitClick = (index) => {
    const updatedLimits = localLimits.filter((_, i) => i !== index);
    setLocalLimits(updatedLimits);
    handleInputChange("limits", updatedLimits);
  };

  const handleSaveClick = () => {
    handleUpdateLimit();
  };

  const isUsageLimitRequired = localUsageLimitRequired === true;

  if (loading) {
    return (
      <div className="usage-limit__container">
        <div className="usage-limit__sidebar">
          <Sidebar />
        </div>
        <div className="usage-limit__main">
          <div className="usage-limit__loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="usage-limit__container">
      <div className="usage-limit__sidebar">
        <Sidebar />
      </div>

      <div className="usage-limit__main">
        <div className="usage-limit__header">
          <div className="usage-limit__header-left">
            <button className="usage-limit__back-btn" onClick={handleBackClick}>
              ←
            </button>
            <h2 className="usage-limit__title">Usage Limit</h2>
            <span className="usage-limit__corp-id">Corporate ID: {corporateId}</span>
          </div>
          
          <div className="usage-limit__header-right">
            {isEditMode && (
              <div className="usage-limit__edit-actions">
                <button className="usage-limit__cancel-btn" onClick={handleCancelClick}>
                  Cancel
                </button>
                <button className="usage-limit__save-btn" onClick={handleSaveClick}>
                  Save Changes
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="usage-limit__content">
          <div className="usage-limit__page-header">
            <h1 className="usage-limit__page-title">Usage Limit</h1>
            <p className="usage-limit__page-subtitle">
              Define spending thresholds and transaction volume controls for this corporate account.
            </p>
          </div>

          <div className="usage-limit__card">
            <div className="usage-limit__card-header">
              <div className="usage-limit__corp-info">
                <div className="usage-limit__info-label">CORPORATE</div>
                <div className="usage-limit__corp-name">{`Corporate ${corporateId}`}</div>
              </div>
            </div>

            <div className="usage-limit__card-body">
              <div className="usage-limit__grid">
                {/* Usage Limit Required */}
                <div className="usage-limit__grid-item usage-limit__full-width">
                  <div className="usage-limit__field-label">USAGE LIMIT REQUIRED</div>
                  {isEditMode ? (
                    <div className="usage-limit__radio-group">
                      <label className="usage-limit__radio-label">
                        <input
                          type="radio"
                          name="usageLimitRequired"
                          value="true"
                          checked={localUsageLimitRequired === true}
                          onChange={(e) => handleUsageLimitRequiredChange(e.target.value)}
                          className="usage-limit__radio-input"
                        />
                        <span className="usage-limit__radio-custom"></span>
                        <span className="usage-limit__radio-text">Yes</span>
                      </label>
                      <label className="usage-limit__radio-label">
                        <input
                          type="radio"
                          name="usageLimitRequired"
                          value="false"
                          checked={localUsageLimitRequired === false}
                          onChange={(e) => handleUsageLimitRequiredChange(e.target.value)}
                          className="usage-limit__radio-input"
                        />
                        <span className="usage-limit__radio-custom"></span>
                        <span className="usage-limit__radio-text">No</span>
                      </label>
                    </div>
                  ) : (
                    <div className={`usage-limit__status-badge ${usageLimitData?.usageLimitRequired ? "usage-limit__status-yes" : "usage-limit__status-no"}`}>
                      {usageLimitData?.usageLimitRequired ? "Yes" : "No"}
                    </div>
                  )}
                </div>

                {/* Limits Section - Hide edit controls for role_id 15 */}
                {isUsageLimitRequired && (
                  <>
                    <div className="usage-limit__full-width">
                      <div className="usage-limit__limits-header">
                        <div className="usage-limit__field-label">LIMITS CONFIGURATION</div>
                        {/* Show "Set New Limit" button only if user can perform actions */}
                        {isEditMode && canAddMoreLimits() && canPerformActions && (
                          <button className="usage-limit__add-limit-btn" onClick={handleAddNewLimit} type="button">
                            + Set New Limit
                          </button>
                        )}
                      </div>
                      
                      {localLimits && localLimits.length > 0 ? (
                        localLimits.map((limit, index) => (
                          <div key={index} className="usage-limit__limit-item">
                            <div className="usage-limit__limit-header">
                              <div className="usage-limit__limit-title">
                                {limit.limitOn === "AMOUNT" ? "💰 Amount Limit" : "🔄 Transaction Limit"}
                              </div>
                              {/* Show Remove button only if user can perform actions and more than 1 limit */}
                              {isEditMode && localLimits.length > 1 && canPerformActions && (
                                <button className="usage-limit__remove-limit-btn" onClick={() => handleRemoveLimitClick(index)} type="button">
                                  ✕ Remove
                                </button>
                              )}
                            </div>
                            
                            <div className="usage-limit__limit-fields">
                              {/* Limit Type - Disable radio buttons for role_id 15 in edit mode */}
                              <div className="usage-limit__limit-field">
                                <div className="usage-limit__field-label-small">LIMIT TYPE</div>
                                {isEditMode ? (
                                  <div className="usage-limit__radio-group-small">
                                    <label className="usage-limit__radio-label-small">
                                      <input
                                        type="radio"
                                        name={`limitOn_${index}`}
                                        value="AMOUNT"
                                        checked={limit.limitOn === "AMOUNT"}
                                        onChange={(e) => handleLimitOnChange(index, e.target.value)}
                                        className="usage-limit__radio-input-small"
                                        disabled={!canPerformActions || (limit.limitOn === "AMOUNT" && localLimits.some(l => l.limitOn === "TRANSACTION"))}
                                      />
                                      <span className="usage-limit__radio-custom-small"></span>
                                      <span className="usage-limit__radio-text-small">Amount</span>
                                    </label>
                                    <label className="usage-limit__radio-label-small">
                                      <input
                                        type="radio"
                                        name={`limitOn_${index}`}
                                        value="TRANSACTION"
                                        checked={limit.limitOn === "TRANSACTION"}
                                        onChange={(e) => handleLimitOnChange(index, e.target.value)}
                                        className="usage-limit__radio-input-small"
                                        disabled={!canPerformActions || (limit.limitOn === "TRANSACTION" && localLimits.some(l => l.limitOn === "AMOUNT"))}
                                      />
                                      <span className="usage-limit__radio-custom-small"></span>
                                      <span className="usage-limit__radio-text-small">Transaction</span>
                                    </label>
                                  </div>
                                ) : (
                                  <div className="usage-limit__type-badge">
                                    {getLimitTypeDisplay(limit.limitOn)}
                                  </div>
                                )}
                              </div>

                              {/* Frequency - Disable buttons for role_id 15 in edit mode */}
                              <div className="usage-limit__limit-field">
                                <div className="usage-limit__field-label-small">FREQUENCY</div>
                                {isEditMode ? (
                                  <div className="usage-limit__radio-group-small">
                                    <label className="usage-limit__radio-label-small">
                                      <input
                                        type="radio"
                                        name={`frequency_${index}`}
                                        value="HOURS"
                                        checked={limit.frequency === "HOURS"}
                                        onChange={(e) => handleFrequencyChange(index, e.target.value)}
                                        className="usage-limit__radio-input-small"
                                        disabled={!canPerformActions}
                                      />
                                      <span className="usage-limit__radio-custom-small"></span>
                                      <span className="usage-limit__radio-text-small">Hour</span>
                                    </label>
                                    <label className="usage-limit__radio-label-small">
                                      <input
                                        type="radio"
                                        name={`frequency_${index}`}
                                        value="DAYS"
                                        checked={limit.frequency === "DAYS"}
                                        onChange={(e) => handleFrequencyChange(index, e.target.value)}
                                        className="usage-limit__radio-input-small"
                                        disabled={!canPerformActions}
                                      />
                                      <span className="usage-limit__radio-custom-small"></span>
                                      <span className="usage-limit__radio-text-small">Day</span>
                                    </label>
                                    <label className="usage-limit__radio-label-small">
                                      <input
                                        type="radio"
                                        name={`frequency_${index}`}
                                        value="MONTHS"
                                        checked={limit.frequency === "MONTHS"}
                                        onChange={(e) => handleFrequencyChange(index, e.target.value)}
                                        className="usage-limit__radio-input-small"
                                        disabled={!canPerformActions}
                                      />
                                      <span className="usage-limit__radio-custom-small"></span>
                                      <span className="usage-limit__radio-text-small">Month</span>
                                    </label>
                                  </div>
                                ) : (
                                  <div className="usage-limit__frequency-badge">
                                    {getFrequencyDisplay(limit.frequency)}
                                  </div>
                                )}
                              </div>

                              {/* Duration - Disable input for role_id 15 in edit mode */}
                              <div className="usage-limit__limit-field">
                                <div className="usage-limit__field-label-small">DURATION</div>
                                {isEditMode ? (
                                  <div className="usage-limit__duration-wrapper">
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      value={limit.duration || ""}
                                      onChange={(e) => handleDurationChange(index, e.target.value)}
                                      onWheel={handleWheel}
                                      onKeyDown={handleKeyDown}
                                      className="usage-limit__duration-field"
                                      placeholder="Enter duration"
                                      disabled={!canPerformActions}
                                    />
                                    <span className="usage-limit__duration-unit">
                                      {getDurationUnit(limit.frequency)}
                                    </span>
                                  </div>
                                ) : (
                                  <div className="usage-limit__duration-value">
                                    {limit.duration ? `${limit.duration} ${getFrequencyDisplay(limit.frequency)}${parseInt(limit.duration) > 1 ? 's' : ''}` : "N/A"}
                                  </div>
                                )}
                              </div>

                              {/* Value - Disable input for role_id 15 in edit mode */}
                              <div className="usage-limit__limit-field">
                                <div className="usage-limit__field-label-small">LIMIT VALUE</div>
                                {isEditMode ? (
                                  <div className="usage-limit__input-wrapper">
                                    <span className="usage-limit__currency">₹</span>
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      value={limit.value || ""}
                                      onChange={(e) => handleNumberInputChange(index, e)}
                                      onWheel={handleWheel}
                                      onKeyDown={handleKeyDown}
                                      className="usage-limit__input-field"
                                      placeholder="Enter limit value"
                                      disabled={!canPerformActions}
                                    />
                                  </div>
                                ) : (
                                  <div className="usage-limit__limit-value">
                                    ₹{limit.value || "N/A"}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="usage-limit__no-limits-message">
                          <div className="usage-limit__no-limits-text">
                            No limits configured yet.
                            {isEditMode && canPerformActions && " Click 'Set New Limit' to create one."}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Show Update/Configure buttons only if user can perform actions */}
              {!isEditMode && isUsageLimitRequired && canPerformActions && (
                <div className="usage-limit__action-section">
                  <button className="usage-limit__update-btn" onClick={handleEditClick}>
                    Update Limits
                  </button>
                </div>
              )}
              
              {!isEditMode && !isUsageLimitRequired && canPerformActions && (
                <div className="usage-limit__action-section">
                  <button className="usage-limit__update-btn" onClick={handleEditClick}>
                    Configure Limits
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageLimitComponent;